/**
 * Klantenportaal - Publieke pagina voor klanten
 * Toont boten, onderdelen en gebruiksaanwijzingen
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';
import './KlantPortaal.css';

interface OnderhoudsOnderdeel {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  artikelnummer: string | null;
  omschrijving: string | null;
  vervangingsinterval_uren: number | null;
  vervangingsinterval_maanden: number | null;
  /* Wat er in dit onderdeel zit. Een servicekit is een product met de losse filters
     eronder; die staan hier en zijn in de tabel uit te klappen. */
  onderdelen: OnderhoudsOnderdeel[];
}

/* Het catalogusproduct achter een gemonteerd onderdeel. De motorvelden staan er los
   in en zijn leeg bij alles wat geen motor is. */
interface Product {
  id: number;
  fabrikant: string | null;
  model: string | null;
  type: string | null;
  vermogen_pk: number | null;
  vermogen_kw: number | null;
  cilinders: number | null;
  omschrijving: string | null;
  /* Waar het product in de catalogus staat; de lijst wordt hierop gebundeld. */
  categorie: string | null;
  categorie_volgorde: number | null;
  hoofdgroep: string | null;
}

/* Alles wat in de boot gemonteerd zit: motor, generator, pomp, filter. Tot
   23-09-2026 stonden motoren in een aparte lijst met positie, serienummer en
   draaiuren; die drie gelden voor elk gemonteerd ding en staan nu hier. */
interface BootOnderdeel {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  artikelnummer: string | null;
  omschrijving: string | null;
  positie: string | null;
  serienummer: string | null;
  bouwjaar: number | null;
  installatie_datum: string | null;
  installatie_draaiuren: number | null;
  draaiuren_huidig: number | null;
  laatste_onderhoud_datum: string | null;
  laatste_onderhoud_uren: number | null;
  volgende_onderhoud_uren: number | null;
  levensduur_uren: number | null;
  levensduur_maanden: number | null;
  vervangingsdatum: string | null;
  garantie_tot: string | null;
  handleiding_url: string | null;
  notities: string | null;
  is_vervangen: boolean;
  product: Product | null;
  /* Wat er periodiek aan vervangen moet worden, uit de productkoppelingen. */
  onderhoudsonderdelen: OnderhoudsOnderdeel[];
  /* Handleidingen uit de catalogus, via voorraad_item_id van het onderdeel. */
  documenten: OnderdeelDocument[];
  dagen_tot_vervanging: number | null;
  uren_tot_vervanging: number | null;
}

interface OnderdeelDocument {
  id: number;
  document_type: string;
  titel: string | null;
  taal: string | null;
  versie: string | null;
  omschrijving: string | null;
}

interface Boot {
  id: number;
  bootnaam: string | null;
  registratienummer: string | null;
  bouwjaar: number | null;
  lengte: number | null;
  breedte: number | null;
  diepgang: number | null;
  materiaal: string | null;
  ligplaats: string | null;
  boot_model_fabrikant: string;
  boot_model_naam: string;
  onderdelen: BootOnderdeel[];
  documenten: BootDocument[];
}

/* Scheepsdocumenten die de klant mag zien. De backend stuurt alleen documenten
   mee waarvan het vinkje "zichtbaar in klantportaal" aan staat. */
interface BootDocument {
  id: number;
  document_type: string;
  titel: string | null;
  taal: string | null;
  versie: string | null;
  omschrijving: string | null;
}

interface Offerte {
  id: number;
  offertenummer: string;
  status: string;
  offerte_datum: string;
  geldig_tot: string;
  subtotaal: number;
  btw_bedrag: number;
  totaal: number;
  boot_naam: string | null;
  pdf_beschikbaar: boolean;
}

interface Factuur {
  id: number;
  factuurnummer: string;
  status: string;
  factuur_datum: string;
  vervaldatum: string;
  subtotaal: number;
  btw_bedrag: number;
  totaal: number;
  bedrag_betaald: number;
  bedrag_openstaand: number;
  boot_naam: string | null;
  pdf_beschikbaar: boolean;
}

interface PortaalData {
  klant_naam: string;
  klant_email: string | null;
  boten: Boot[];
  offertes: Offerte[];
  facturen: Factuur[];
  laatste_update: string;
}

const KlantPortaal = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PortaalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBoots, setExpandedBoots] = useState<number[]>([]);
  /* Eén onderdeel tegelijk open: bij vijftien onderdelen met elk hun documentatie wordt
     de pagina anders een muur. Een nieuwe openen sluit de vorige. Besluit Dylan 25-09-2026. */
  const [openOnderdeel, setOpenOnderdeel] = useState<number | null>(null);
  /* Documenten gaan per soort onder een uitklapbare kop, net als de onderdelen.
     Twaalf kaartjes onder elkaar is geen overzicht. */
  const [openDocumentSoort, setOpenDocumentSoort] = useState<string | null>(null);
  /* De twee grote blokken onder een boot staan dicht en er is er een tegelijk open.
     Met twaalf documenten en vijftien onderdelen werd de pagina anders een muur,
     ook met de boten zelf ingeklapt. Besluit Dylan 01-11-2026. De sleutel draagt het
     bootnummer mee, zodat twee boten elkaars blokken niet openen. */
  const [openBlok, setOpenBlok] = useState<string | null>(null);
  /* Binnen Gemonteerde onderdelen klapt een categorie tegelijk uit. */
  const [openCategorie, setOpenCategorie] = useState<string | null>(null);
  const [expandedKits, setExpandedKits] = useState<number[]>([]);

  useEffect(() => {
    if (token) {
      loadPortaalData();
    }
  }, [token]);

  const loadPortaalData = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/klant-portaal/view/${token}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Kon portaal niet laden');
      }

      const portaalData = await response.json();
      setData(portaalData);

      // Bij een enkele boot staat die open; bij meerdere alles dicht
      if (portaalData.boten.length === 1) {
        setExpandedBoots([portaalData.boten[0].id]);
      } else {
        setExpandedBoots([]);
      }
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  /* Een nieuwe boot openen sluit de vorige: zelfde regel als bij de onderdelen
     en de documenten, anders wordt de pagina bij meerdere boten een muur. */
  const toggleBoot = (bootId: number) => {
    setExpandedBoots(prev => (prev.includes(bootId) ? [] : [bootId]));
    setOpenBlok(null);
    setOpenCategorie(null);
    setOpenDocumentSoort(null);
    setOpenOnderdeel(null);
  };

  const toggleBlok = (sleutel: string) => {
    setOpenBlok(prev => (prev === sleutel ? null : sleutel));
    setOpenCategorie(null);
    setOpenDocumentSoort(null);
    setOpenOnderdeel(null);
  };

  const toggleCategorie = (sleutel: string) => {
    setOpenCategorie(prev => (prev === sleutel ? null : sleutel));
    setOpenOnderdeel(null);
  };

  const toggleKit = (koppelingId: number) => {
    setExpandedKits(prev =>
      prev.includes(koppelingId)
        ? prev.filter(id => id !== koppelingId)
        : [...prev, koppelingId]
    );
  };

  const toggleOnderdeel = (onderdeelId: number) => {
    setOpenOnderdeel(prev => (prev === onderdeelId ? null : onderdeelId));
    // Servicekits van het vorige onderdeel hoeven niet open te blijven staan.
    setExpandedKits([]);
  };

  /* Een onderhoudsonderdeel als tabelrij, met zijn inhoud eronder. Een servicekit is
     een product met de losse filters eronder; die rij krijgt een driehoekje en klapt
     open. Geeft een lijst rijen terug omdat een <tr> geen kinderen mag bevatten. */
  const onderhoudsRijen = (deel: OnderhoudsOnderdeel, niveau: number): JSX.Element[] => {
    const inhoud = deel.onderdelen || [];
    const uitklapbaar = inhoud.length > 0;
    const open = expandedKits.includes(deel.id);

    const interval = [
      deel.vervangingsinterval_uren ? `${deel.vervangingsinterval_uren} uren` : null,
      deel.vervangingsinterval_maanden ? `${deel.vervangingsinterval_maanden} mnd` : null,
    ].filter(Boolean).join(' / ');

    const rij = (
      <tr
        key={deel.id}
        className={[
          uitklapbaar ? 'onderhoud-kit' : '',
          niveau > 0 ? 'onderhoud-kind' : '',
        ].filter(Boolean).join(' ') || undefined}
        onClick={uitklapbaar ? () => toggleKit(deel.id) : undefined}
      >
        {/* Het niveau als custom property, niet als padding-left: een inline stijl wint
            altijd van een media query, en op een telefoon wordt deze tabel een lijst
            kaarten waar die inspringing juist weg moet. */}
        <td className="kit-type" style={{ '--niveau': niveau } as React.CSSProperties}>
          {deel.onderdeel_type}
        </td>
        <td data-label="Artikelnr">{deel.artikelnummer || '-'}</td>
        <td className="kit-omschrijving">{deel.omschrijving || '-'}</td>
        <td data-label="Interval">{interval || '-'}</td>
        {/* De badge rechts is zelf de knop; een pijl ernaast zei hetzelfde nog eens. */}
        <td className="kit-actie">
          {uitklapbaar && (
            <span className="kit-aantal">{inhoud.length} onderdelen</span>
          )}
        </td>
      </tr>
    );

    if (!uitklapbaar || !open) return [rij];
    return [rij, ...inhoud.flatMap(kind => onderhoudsRijen(kind, niveau + 1))];
  };

  /* De gemonteerde onderdelen gebundeld per categorie uit de catalogus, in dezelfde
     volgorde als het menu Producten. Was een platte lijst van vijftien regels. */
  const perDocumentSoort = (lijst: BootDocument[]) => {
    const bakjes = new Map<string, BootDocument[]>();
    lijst.forEach(d => {
      const soort = d.document_type || 'Overig';
      bakjes.set(soort, [...(bakjes.get(soort) || []), d]);
    });
    return [...bakjes.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  };

  const perCategorie = (lijst: BootOnderdeel[]) => {
    type Bakje = { volgorde: number; groepen: Map<string, BootOnderdeel[]> };
    const bakjes = new Map<string, Bakje>();
    lijst.forEach(o => {
      const categorie = o.product?.categorie || 'Overig';
      const groep = o.product?.hoofdgroep || categorie;
      const volgorde = o.product?.categorie_volgorde ?? 999999;
      const bakje = bakjes.get(categorie) || { volgorde, groepen: new Map() };
      bakje.groepen.set(groep, [...(bakje.groepen.get(groep) || []), o]);
      bakjes.set(categorie, bakje);
    });
    return [...bakjes.entries()]
      .sort((a, b) => a[1].volgorde - b[1].volgorde)
      .map(([naam, bakje]) => ({
        naam,
        groepen: [...bakje.groepen.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (dagenTot: number | null, urenTot: number | null) => {
    if (dagenTot !== null && dagenTot < 0) return '#dc2626'; // Rood - verlopen
    if (dagenTot !== null && dagenTot < 30) return '#f59e0b'; // Oranje - bijna
    if (urenTot !== null && urenTot < 0) return '#dc2626'; // Rood - verlopen
    if (urenTot !== null && urenTot < 50) return '#f59e0b'; // Oranje - bijna
    return '#16a34a'; // Groen - ok
  };

  if (loading) {
    return (
      <div className="portaal-container">
        <div className="portaal-loading">
          <div className="portaal-header">
            <h1>
              <span style={{ color: '#e06c00' }}>Buro</span>
              <span style={{ color: '#1f6fd0' }}>42</span>
              <span style={{ marginLeft: '10px', fontSize: '0.6em', color: '#666' }}>Klantenportaal</span>
            </h1>
          </div>
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            Gegevens laden...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portaal-container">
        <div className="portaal-header">
          <h1>
            <span style={{ color: '#e06c00' }}>Buro</span>
            <span style={{ color: '#1f6fd0' }}>42</span>
            <span style={{ marginLeft: '10px', fontSize: '0.6em', color: '#666' }}>Klantenportaal</span>
          </h1>
        </div>
        <div className="portaal-error">
          <h2>Toegang Geweigerd</h2>
          <p>{error}</p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
            Neem contact op met Buro 42 als u denkt dat dit een fout is.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="portaal-container">
      <div className="portaal-header">
        <h1>
          <span style={{ color: '#e06c00' }}>Buro</span>
          <span style={{ color: '#1f6fd0' }}>42</span>
          <span style={{ marginLeft: '10px', fontSize: '0.6em', color: '#666' }}>Klantenportaal</span>
        </h1>
      </div>

      <div className="portaal-welcome">
        <h2>Welkom, {data.klant_naam}</h2>
        <p>{data.boten.length > 0
          ? 'Hieronder vindt u een overzicht van uw boot(en) met alle geïnstalleerde onderdelen en bijbehorende documentatie.'
          : 'Hieronder vindt u uw facturen en documenten.'}</p>
      </div>

      {data.boten.length === 0 ? null : (
        <div className="portaal-boten">
          {data.boten.map(boot => (
            <div key={boot.id} className="boot-card">
              <div
                className="boot-header"
                onClick={() => toggleBoot(boot.id)}
              >
                <div className="boot-title">
                  <h3>{boot.bootnaam || 'Onbekende boot'}</h3>
                  <span className="boot-model">
                    {boot.boot_model_fabrikant} {boot.boot_model_naam}
                  </span>
                </div>
                <div className="boot-meta">
                  {boot.registratienummer && (
                    <span className="meta-item">Reg: {boot.registratienummer}</span>
                  )}
                  {boot.bouwjaar && (
                    <span className="meta-item">Bouwjaar: {boot.bouwjaar}</span>
                  )}
                  <span className="expand-icon">
                    {expandedBoots.includes(boot.id) ? '▼' : '▶'}
                  </span>
                </div>
              </div>

              {expandedBoots.includes(boot.id) && (
                <div className="boot-content">
                  {/* Boot specificaties */}
                  <div className="boot-specs">
                    <h4>Specificaties</h4>
                    <div className="specs-grid">
                      {boot.lengte && <div><strong>Lengte:</strong> {boot.lengte}m</div>}
                      {boot.breedte && <div><strong>Breedte:</strong> {boot.breedte}m</div>}
                      {boot.diepgang && <div><strong>Diepgang:</strong> {boot.diepgang}m</div>}
                      {boot.materiaal && <div><strong>Materiaal:</strong> {boot.materiaal}</div>}
                      {boot.ligplaats && <div><strong>Ligplaats:</strong> {boot.ligplaats}</div>}
                    </div>
                  </div>

                  {/* Scheepsdocumenten - zelfde opmaak als de andere blokken:
                      blauwe kop, kaartjes in een raster. */}
                  {boot.documenten && boot.documenten.length > 0 && (
                    <div className="boot-documenten">
                      <h4
                        className="blok-kop"
                        onClick={() => toggleBlok(`${boot.id}:documenten`)}
                      >
                        <span>Documenten</span>
                        <span className="blok-rechts">
                          <span className="document-aantal">{boot.documenten.length}</span>
                          <span className="expand-icon">
                            {openBlok === `${boot.id}:documenten` ? '▼' : '▶'}
                          </span>
                        </span>
                      </h4>
                      {openBlok === `${boot.id}:documenten` && perDocumentSoort(boot.documenten).map(([soort, documenten]) => (
                        <div key={soort} className="document-soortgroep">
                          <div
                            className="document-soort-kop"
                            onClick={() => setOpenDocumentSoort(
                              openDocumentSoort === soort ? null : soort
                            )}
                          >
                            <span>{soort}</span>
                            <span className="document-aantal">{documenten.length}</span>
                          </div>
                          {openDocumentSoort === soort && (
                      <div className="documenten-list">
                        {documenten.map(doc => (
                          <a
                            key={doc.id}
                            className="document-card"
                            href={`${getApiBaseUrl()}/api/v1/klant-portaal/document/${token}/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="document-icoon">📄</span>
                            <span className="document-tekst">
                              <span className="document-titel">{doc.titel || doc.document_type}</span>
                              <span className="document-soort">
                                {doc.document_type}
                                {doc.versie ? ` · versie ${doc.versie}` : ''}
                                {doc.taal ? ` · ${doc.taal}` : ''}
                              </span>
                              {doc.omschrijving && (
                                <span className="document-omschrijving">{doc.omschrijving}</span>
                              )}
                            </span>
                          </a>
                        ))}
                      </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Gemonteerde onderdelen. Sinds 23-09-2026 één lijst: de motor
                      stond hierboven in een eigen blok, maar is gewoon een onderdeel
                      met een positie, een serienummer en draaiuren. */}
                  {boot.onderdelen.length > 0 && (
                    <div className="boot-onderdelen">
                      <h4
                        className="blok-kop"
                        onClick={() => toggleBlok(`${boot.id}:onderdelen`)}
                      >
                        <span>Gemonteerde onderdelen</span>
                        <span className="blok-rechts">
                          <span className="document-aantal">{boot.onderdelen.length}</span>
                          <span className="expand-icon">
                            {openBlok === `${boot.id}:onderdelen` ? '▼' : '▶'}
                          </span>
                        </span>
                      </h4>
                      {openBlok === `${boot.id}:onderdelen` && perCategorie(boot.onderdelen).map(bak => {
                        const categorieSleutel = `${boot.id}:${bak.naam}`;
                        const categorieOpen = openCategorie === categorieSleutel;
                        const aantal = bak.groepen.reduce((som, [, lijst]) => som + lijst.length, 0);
                        return (
                        <div key={bak.naam} className="onderdeel-categorie">
                          <h5
                            className="categorie-kop"
                            onClick={() => toggleCategorie(categorieSleutel)}
                          >
                            <span>{bak.naam}</span>
                            <span className="blok-rechts">
                              <span className="document-aantal">{aantal}</span>
                              <span className="expand-icon">{categorieOpen ? '▼' : '▶'}</span>
                            </span>
                          </h5>
                          {categorieOpen && bak.groepen.map(([groepNaam, onderdelen]) => (
                            <div key={groepNaam} className="onderdeel-groep">
                              {/* De hoofdgroep alleen tonen als hij iets toevoegt: bij
                                  een categorie met een enkele gelijknamige groep is de
                                  kop erboven al genoeg. */}
                              {!(bak.groepen.length === 1 && groepNaam === bak.naam) && (
                                <h6 className="groep-kop">{groepNaam}</h6>
                              )}
                      {onderdelen.map(onderdeel => {
                        const uitgeklapt = openOnderdeel === onderdeel.id;
                        const naam = onderdeel.product
                          ? `${onderdeel.product.fabrikant || ''} ${onderdeel.product.model || ''}`.trim()
                          : '';
                        return (
                        <div key={onderdeel.id} className="motor-card">
                          <div
                            className="motor-header"
                            onClick={() => toggleOnderdeel(onderdeel.id)}
                          >
                            <div className="motor-info">
                              <span
                                className="onderdeel-status"
                                style={{ backgroundColor: getStatusColor(onderdeel.dagen_tot_vervanging, onderdeel.uren_tot_vervanging) }}
                              />
                              <strong>{onderdeel.onderdeel_type}</strong>
                              {naam && <span className="onderdeel-naam">{naam}</span>}
                              {onderdeel.positie && <span className="motor-position">({onderdeel.positie})</span>}
                            </div>
                            <div className="motor-stats">
                              {onderdeel.draaiuren_huidig != null && <span>{onderdeel.draaiuren_huidig} uren</span>}
                              {onderdeel.product?.vermogen_pk && <span>{onderdeel.product.vermogen_pk} PK</span>}
                              <span className="expand-icon">{uitgeklapt ? '▼' : '▶'}</span>
                            </div>
                          </div>

                          {uitgeklapt && (
                            <div className="motor-content">
                              <div className="motor-details">
                                {onderdeel.fabrikant && <div><strong>Fabrikant:</strong> {onderdeel.fabrikant}</div>}
                                {onderdeel.artikelnummer && <div><strong>Artikelnr:</strong> {onderdeel.artikelnummer}</div>}
                                {onderdeel.serienummer && <div><strong>Serienummer:</strong> {onderdeel.serienummer}</div>}
                                {onderdeel.bouwjaar && <div><strong>Bouwjaar:</strong> {onderdeel.bouwjaar}</div>}
                                {onderdeel.installatie_datum && (
                                  <div><strong>Geïnstalleerd:</strong> {formatDate(onderdeel.installatie_datum)}</div>
                                )}
                                {onderdeel.laatste_onderhoud_datum && (
                                  <div>
                                    <strong>Laatste onderhoud:</strong> {formatDate(onderdeel.laatste_onderhoud_datum)}
                                    {onderdeel.laatste_onderhoud_uren != null && ` (${onderdeel.laatste_onderhoud_uren} uren)`}
                                  </div>
                                )}
                                {onderdeel.volgende_onderhoud_uren != null && (
                                  <div><strong>Volgende onderhoud:</strong> bij {onderdeel.volgende_onderhoud_uren} uren</div>
                                )}
                                {onderdeel.vervangingsdatum && (
                                  <div>
                                    <strong>Vervangen voor:</strong> {formatDate(onderdeel.vervangingsdatum)}
                                    {onderdeel.dagen_tot_vervanging !== null && (
                                      <span className={`dagen-indicator ${onderdeel.dagen_tot_vervanging < 0 ? 'verlopen' : onderdeel.dagen_tot_vervanging < 30 ? 'bijna' : 'ok'}`}>
                                        {onderdeel.dagen_tot_vervanging < 0
                                          ? `${Math.abs(onderdeel.dagen_tot_vervanging)} dagen over datum!`
                                          : `nog ${onderdeel.dagen_tot_vervanging} dagen`}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {onderdeel.uren_tot_vervanging !== null && (
                                  <div>
                                    <strong>Uren tot vervanging:</strong>
                                    <span className={`uren-indicator ${onderdeel.uren_tot_vervanging < 0 ? 'verlopen' : onderdeel.uren_tot_vervanging < 50 ? 'bijna' : 'ok'}`}>
                                      {onderdeel.uren_tot_vervanging < 0
                                        ? `${Math.abs(onderdeel.uren_tot_vervanging)} uren over!`
                                        : `nog ${onderdeel.uren_tot_vervanging} uren`}
                                    </span>
                                  </div>
                                )}
                                {onderdeel.garantie_tot && (
                                  <div><strong>Garantie tot:</strong> {formatDate(onderdeel.garantie_tot)}</div>
                                )}
                                {onderdeel.notities && (
                                  <div><strong>Notities:</strong> {onderdeel.notities}</div>
                                )}
                              </div>

                              {/* Wat er periodiek aan vervangen moet worden. Hing vroeger
                                  alleen onder een motor; geldt nu voor elk product met
                                  onderdeelkoppelingen. */}
                              {onderdeel.onderhoudsonderdelen.length > 0 && (
                                <div className="motor-onderdelen">
                                  <h5>Onderhoudsonderdelen</h5>
                                  <table>
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th>Artikelnr</th>
                                        <th>Omschrijving</th>
                                        <th>Interval</th>
                                        <th aria-label="Uitklappen" />
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {onderdeel.onderhoudsonderdelen.map(deel =>
                                        onderhoudsRijen(deel, 0))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* Documentatie */}
                              {(onderdeel.documenten.length > 0 || onderdeel.handleiding_url) && (
                                <div className="motor-handboeken">
                                  <h5>Documentatie</h5>
                                  <div className="handboeken-list">
                                    {onderdeel.handleiding_url && (
                                      <a
                                        className="handboek-item"
                                        href={onderdeel.handleiding_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                      >
                                        <div className="handboek-icon">📖</div>
                                        <div className="handboek-info">
                                          <strong>Gebruiksaanwijzing</strong>
                                        </div>
                                      </a>
                                    )}
                                    {onderdeel.documenten.map(doc => (
                                      <a
                                        key={doc.id}
                                        className="handboek-item"
                                        href={`${getApiBaseUrl()}/api/v1/klant-portaal/onderdeel-document/${token}/${doc.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                      >
                                        <div className="handboek-icon">📄</div>
                                        <div className="handboek-info">
                                          <strong>{doc.document_type}</strong>
                                          {doc.titel && <div className="handboek-titel">{doc.titel}</div>}
                                          {doc.taal && <span className="handboek-taal">{doc.taal}</span>}
                                        </div>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        );
                      })}
                            </div>
                          ))}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Offertes sectie */}
      {data.offertes && data.offertes.length > 0 && (
        <div className="portaal-offertes">
          <h3>Uw Offertes</h3>
          <div className="offertes-list">
            {data.offertes.map(offerte => (
              <div key={offerte.id} className="offerte-card">
                <div className="offerte-header">
                  <h4>{offerte.offertenummer}</h4>
                  <span className={`offerte-status offerte-status-${offerte.status.toLowerCase()}`}>
                    {offerte.status}
                  </span>
                </div>
                <div className="offerte-details">
                  <div className="offerte-info">
                    {offerte.boot_naam && (
                      <div><strong>Boot:</strong> {offerte.boot_naam}</div>
                    )}
                    <div><strong>Datum:</strong> {formatDate(offerte.offerte_datum)}</div>
                    <div><strong>Geldig tot:</strong> {formatDate(offerte.geldig_tot)}</div>
                  </div>
                  <div className="offerte-bedragen">
                    <div><strong>Subtotaal:</strong> € {offerte.subtotaal.toFixed(2)}</div>
                    <div><strong>BTW:</strong> € {offerte.btw_bedrag.toFixed(2)}</div>
                    <div className="offerte-totaal"><strong>Totaal:</strong> € {offerte.totaal.toFixed(2)}</div>
                  </div>
                </div>
                <div className="offerte-actions">
                  <button
                    onClick={() => navigate(`/portaal/${token}/offerte/${offerte.id}`)}
                    className="btn-view-pdf"
                  >
                    👁️ Bekijken
                  </button>
                  {offerte.pdf_beschikbaar ? (
                    <a
                      href={`${getApiBaseUrl()}/api/v1/klant-portaal/offerte/${token}/${offerte.id}/pdf`}
                      download
                      className="btn-download-pdf"
                    >
                      📄 Download PDF
                    </a>
                  ) : (
                    <span className="pdf-niet-beschikbaar">PDF niet beschikbaar</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Facturen sectie */}
      {data.facturen && data.facturen.length > 0 && (
        <div className="portaal-facturen">
          <h3>Uw Facturen</h3>
          <div className="facturen-list">
            {data.facturen.map(factuur => {
              const getFactuurStatusStyle = (status: string) => {
                switch (status) {
                  case 'VERSTUURD':
                    return { bg: '#dbeafe', color: '#1d4ed8', label: 'Verstuurd' };
                  case 'BETAALD':
                    return { bg: '#d1fae5', color: '#059669', label: 'Betaald' };
                  case 'DEELS_BETAALD':
                    return { bg: '#fef3c7', color: '#d97706', label: 'Deels betaald' };
                  case 'VERVALLEN':
                    return { bg: '#fee2e2', color: '#dc2626', label: 'Vervallen' };
                  default:
                    return { bg: '#f3f4f6', color: '#4b5563', label: status };
                }
              };
              const statusStyle = getFactuurStatusStyle(factuur.status);

              return (
                <div key={factuur.id} className="factuur-card">
                  <div className="factuur-header">
                    <h4>{factuur.factuurnummer}</h4>
                    <span
                      className="factuur-status"
                      style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                    >
                      {statusStyle.label}
                    </span>
                  </div>
                  <div className="factuur-details">
                    <div className="factuur-info">
                      {factuur.boot_naam && (
                        <div><strong>Boot:</strong> {factuur.boot_naam}</div>
                      )}
                      <div><strong>Factuurdatum:</strong> {formatDate(factuur.factuur_datum)}</div>
                      <div><strong>Vervaldatum:</strong> {formatDate(factuur.vervaldatum)}</div>
                    </div>
                    <div className="factuur-bedragen">
                      <div><strong>Subtotaal:</strong> € {factuur.subtotaal.toFixed(2)}</div>
                      <div><strong>BTW:</strong> € {factuur.btw_bedrag.toFixed(2)}</div>
                      <div className="factuur-totaal"><strong>Totaal:</strong> € {factuur.totaal.toFixed(2)}</div>
                      {factuur.bedrag_openstaand > 0 && (
                        <div className="factuur-openstaand">
                          <strong>Openstaand:</strong> € {factuur.bedrag_openstaand.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="factuur-actions">
                    {factuur.pdf_beschikbaar ? (
                      <a
                        href={`${getApiBaseUrl()}/api/v1/klant-portaal/factuur/${token}/${factuur.id}/pdf`}
                        download
                        className="btn-download-pdf"
                      >
                        📄 Download PDF
                      </a>
                    ) : (
                      <span className="pdf-niet-beschikbaar">PDF niet beschikbaar</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="portaal-footer">
        <p>Laatste update: {new Date(data.laatste_update).toLocaleString('nl-NL')}</p>
        <p>Heeft u vragen? Neem contact op met Buro 42.</p>
      </div>
    </div>
  );
};

export default KlantPortaal;
